/**
 * Rating Service
 *
 * Modern service for puzzle rating functionality.
 * Replaces the legacy AJAX.initErtekeles, AJAX.Ertekeles, AJAX.sendErtekeles functions.
 */

import { ApiClient } from './ApiClient';

/** Rating configuration */
export interface RatingConfig {
    element: HTMLElement;
    solutionId: number;
    max: number;
    saved: number;
}

/** Rating difficulty descriptions (Hungarian) */
const RATING_DESCRIPTIONS = [
    'Elég könnyű', // Very easy
    'Könnyű', // Easy
    'Közepes', // Medium
    'Nehéz', // Hard
    'Nagyon nehéz', // Very hard
] as const;

/** Star image paths */
const STAR_FILLED = 'img/csillag1.png';
const STAR_EMPTY = 'img/csillag0.png';

/**
 * Rating Service for puzzle difficulty ratings.
 */
export class RatingService {
    private apiClient: ApiClient;
    private ratings: Map<string, RatingConfig> = new Map();

    constructor(apiClient?: ApiClient) {
        this.apiClient = apiClient ?? new ApiClient();
    }

    /**
     * Initialize a rating component.
     *
     * @param config - Rating configuration
     */
    init(config: RatingConfig): void {
        const { element, solutionId, max, saved } = config;
        const elementId = element.id || `rating-${solutionId}`;

        // Ensure element has an ID
        if (!element.id) {
            element.id = elementId;
        }

        // Store configuration
        this.ratings.set(elementId, config);

        // Render rating stars
        this.render(elementId, max, saved);
    }

    /**
     * Initialize rating from data attributes (for auto-init pattern).
     *
     * @param element - Element with data-* attributes
     */
    initFromDataAttributes(element: HTMLElement): void {
        const solutionId = parseInt(element.dataset.solutionId ?? '0', 10);
        const max = parseInt(element.dataset.max ?? '0', 10);
        const saved = parseInt(element.dataset.saved ?? '0', 10);

        if (solutionId > 0) {
            this.init({ element, solutionId, max, saved });
        }
    }

    /**
     * Render the rating stars HTML.
     */
    private render(elementId: string, _max: number, saved: number): void {
        const element = document.getElementById(elementId);
        if (!element) return;

        const stars: string[] = [];

        for (let i = 1; i <= 5; i++) {
            const description = RATING_DESCRIPTIONS[i - 1];
            stars.push(
                `<a class="rating-star"
            data-rating="${i}"
            data-element-id="${elementId}"
            title="${description}"
            role="button"
            tabindex="0"
            aria-label="${description}"></a>`,
            );
        }

        element.innerHTML = stars.join('');

        // Add event listeners using event delegation
        element.addEventListener('click', this.handleClick.bind(this));
        element.addEventListener('mouseover', this.handleMouseOver.bind(this));
        element.addEventListener('mouseout', this.handleMouseOut.bind(this));
        element.addEventListener('keydown', this.handleKeyDown.bind(this));

        // Set initial display
        this.updateDisplay(elementId, saved);
    }

    /**
     * Update the star display.
     *
     * @param elementId - The rating element ID
     * @param rating - The rating to display (1-5)
     */
    updateDisplay(elementId: string, rating: number): void {
        const element = document.getElementById(elementId);
        if (!element) return;

        const stars =
            element.querySelectorAll<HTMLAnchorElement>('.rating-star');

        stars.forEach((star, index) => {
            const starImage = index < rating ? STAR_FILLED : STAR_EMPTY;
            star.style.backgroundImage = `url(${starImage})`;
        });
    }

    /**
     * Submit a rating to the server.
     *
     * @param elementId - The rating element ID
     * @param solutionId - The solution ID
     * @param rating - The rating value (1-5)
     * @returns Promise resolving to success status
     */
    async submitRating(
        elementId: string,
        solutionId: number,
        rating: number,
    ): Promise<boolean> {
        try {
            const response = await this.apiClient.get<string>(
                `/ertekeles?fejtesID=${solutionId}&pont=${rating}`,
            );

            if (response.data === '200') {
                // Update saved rating in config
                const config = this.ratings.get(elementId);
                if (config) {
                    config.saved = rating;
                    config.max = rating;
                }

                // Re-render with new saved value
                this.updateDisplay(elementId, rating);
                return true;
            } else {
                // Server returned error message
                const element = document.getElementById(elementId);
                if (element) {
                    element.innerHTML = response.data;
                }
                return false;
            }
        } catch (error) {
            console.error('Failed to submit rating:', error);
            return false;
        }
    }

    /**
     * Handle click on a rating star.
     */
    private handleClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('rating-star')) return;

        event.preventDefault();

        const rating = parseInt(target.dataset.rating ?? '0', 10);
        const elementId = target.dataset.elementId ?? '';
        const config = this.ratings.get(elementId);

        if (config && rating > 0) {
            void this.submitRating(elementId, config.solutionId, rating);
        }
    }

    /**
     * Handle mouse over for preview.
     */
    private handleMouseOver(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('rating-star')) return;

        const rating = parseInt(target.dataset.rating ?? '0', 10);
        const elementId = target.dataset.elementId ?? '';

        if (rating > 0 && elementId) {
            this.updateDisplay(elementId, rating);
        }
    }

    /**
     * Handle mouse out to restore saved rating.
     */
    private handleMouseOut(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('rating-star')) return;

        const elementId = target.dataset.elementId ?? '';
        const config = this.ratings.get(elementId);

        if (config) {
            this.updateDisplay(elementId, config.saved);
        }
    }

    /**
     * Handle keyboard navigation.
     */
    private handleKeyDown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement;
        if (!target.classList.contains('rating-star')) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleClick(event);
        }
    }

    /**
     * Auto-initialize all rating components on the page.
     * Call this on DOMContentLoaded.
     */
    autoInit(): void {
        const ratingElements =
            document.querySelectorAll<HTMLElement>('[data-solution-id]');
        ratingElements.forEach((el) => this.initFromDataAttributes(el));
    }
}

/** Default rating service instance */
export const ratingService = new RatingService();
